/**
 * Page de liste des actions réalisées.
 * Fusionne les actions spontanées et les To Dos réalisés.
 * Supporte la vue equipe pour les squad leads.
 */

import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getActions, deleteAction, updateAction } from "../api/spontaneousActionApi";
import { getTodos, deleteTodo, updateTodo } from "../api/todoApi";
import { getTeamActions, getTeamTodos } from "../api/teamApi";
import { useTeamView } from "../contexts/TeamViewContext";
import SortableTable from "../components/SortableTable";
import ConfirmDialog from "../components/ConfirmDialog";
import { formatDate } from "../utils/dateUtils";
import "./PageStyles.css";

export default function SpontaneousActionListPage() {
  const [items, setItems] = useState([]);
  const [sortBy, setSortBy] = useState("date");
  const [deleteItem, setDeleteItem] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { teamView } = useTeamView();

  useEffect(() => {
    loadItems();
  }, [sortBy, teamView]);

  async function loadItems() {
    const [actionsData, doneTodosData] = await Promise.all([
      teamView ? getTeamActions(mapSortKey(sortBy, "action")) : getActions(mapSortKey(sortBy, "action")),
      teamView ? getTeamTodos(mapSortKey(sortBy, "todo"), "done") : getTodos(mapSortKey(sortBy, "todo"), "done"),
    ]);

    // Normalize actions
    const normalizedActions = actionsData.map((a) => ({
      ...a,
      _type: "action",
      _key: `action-${a.id}`,
      date: a.action_date,
    }));

    // Normalize done todos
    const normalizedTodos = doneTodosData.map((t) => ({
      ...t,
      _type: "todo",
      _key: `todo-${t.id}`,
      date: t.due_date,
      contact_company: t.contact_company,
    }));

    // Merge and sort by date descending
    const merged = [...normalizedActions, ...normalizedTodos];
    merged.sort((a, b) => {
      const key = sortBy;
      if (key === "date") {
        return (b.date || "").localeCompare(a.date || "");
      }
      if (key === "contact_name") {
        return (a.contact_name || "").localeCompare(b.contact_name || "");
      }
      if (key === "contact_company") {
        return (a.contact_company || "").localeCompare(b.contact_company || "");
      }
      if (key === "owner_name") {
        return (a.owner_name || "").localeCompare(b.owner_name || "");
      }
      if (key === "responsable_name") {
        return (a.responsable_name || "").localeCompare(b.responsable_name || "");
      }
      return (b.date || "").localeCompare(a.date || "");
    });

    setItems(merged);
  }

  function mapSortKey(key, type) {
    if (key === "date") return type === "action" ? "action_date" : "due_date";
    if (key === "contact_company") return type === "action" ? "contact_company" : "company";
    if (key === "contact_name") return "contact_name";
    if (key === "owner_name") return "owner_name";
    if (key === "responsable_name") return "responsable_name";
    return type === "action" ? "action_date" : "due_date";
  }

  async function handleDelete() {
    if (!deleteItem) return;
    if (deleteItem._type === "action") {
      await deleteAction(deleteItem.id);
    } else {
      await deleteTodo(deleteItem.id);
    }
    setDeleteItem(null);
    loadItems();
  }

  function startEdit(item) {
    setEditingId(item._key);
    setEditText(item.description || "");
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  async function saveEdit(item) {
    if (editingId !== item._key) return;
    if (editText !== (item.description || "")) {
      if (item._type === "action") {
        await updateAction(item.id, { description: editText });
      } else {
        await updateTodo(item.id, { description: editText });
      }
      setItems((prev) =>
        prev.map((i) => (i._key === item._key ? { ...i, description: editText } : i))
      );
    }
    setEditingId(null);
  }

  const columns = [
    { key: "contact_name", label: "Contact", sortable: true },
    { key: "contact_company", label: "Société", sortable: true },
    { key: "owner_name", label: "Créateur", sortable: true },
    { key: "date", label: "Date", sortable: true },
    { key: "description", label: "Description" },
    { key: "responsable_name", label: "Responsable", sortable: true },
    { key: "source", label: "Source" },
    { key: "tags", label: "Tags" },
    { key: "operations", label: "" },
  ];

  function renderRow(item, _i, hiddenCols) {
    const h = hiddenCols || {};
    // Droit de gestion calculé côté backend (propriétaire, contact public, ou
    // squad/practice lead du propriétaire du contact).
    const canEdit = Boolean(item.can_edit);
    const isEditing = editingId === item._key;
    return (
      <tr key={item._key}>
        {!h.contact_name && (
          <td>
            <a href={`/contacts/${item.contact_id}`} onClick={(e) => { e.preventDefault(); navigate(`/contacts/${item.contact_id}`); }}>
              {item.contact_name || "—"}
            </a>
          </td>
        )}
        {!h.contact_company && <td>{item.contact_company || "—"}</td>}
        {!h.owner_name && <td>{item.owner_name || "—"}</td>}
        {!h.date && <td>{formatDate(item.date)}</td>}
        {!h.description && (
          <td
            className={canEdit && !isEditing ? "cell-editable" : ""}
            onClick={canEdit && !isEditing ? () => startEdit(item) : undefined}
            title={isEditing ? undefined : item.description}
          >
            {isEditing ? (
              <input
                ref={inputRef}
                className="cell-edit-input"
                type="text"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onBlur={() => saveEdit(item)}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setEditingId(null); } }}
              />
            ) : (
              item.description || ""
            )}
          </td>
        )}
        {!h.responsable_name && <td>{item.responsable_name || "—"}</td>}
        {!h.source && (
          <td>
            <span className={`tag-inline ${item._type === "todo" ? "tag-todo-done" : ""}`}>
              {item._type === "todo" ? "To Do" : "Direct"}
            </span>
          </td>
        )}
        {!h.tags && (
          <td>
            <div className="tag-list">
              {item.tags?.map((tag) => (
                <span key={tag.id} className="tag-inline">{tag.label}</span>
              ))}
            </div>
          </td>
        )}
        {!h.operations && (
          <td className="row-actions">
            {canEdit && (
              <div className="row-actions-inner">
                {item._type === "action" && (
                  <button className="btn btn-small" onClick={() => navigate(`/actions/${item.id}/edit`)}>
                    Modifier
                  </button>
                )}
                <button className="btn btn-small btn-danger" onClick={() => setDeleteItem(item)}>
                  Supprimer
                </button>
              </div>
            )}
          </td>
        )}
      </tr>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h2>{teamView ? "Les Réalisés de mon équipe" : "Mes Réalisés"}</h2>
        {!teamView && (
          <Link to="/actions/new" className="btn btn-primary">
            Nouvelle action
          </Link>
        )}
      </div>
      <SortableTable
        columns={columns}
        rows={items}
        renderRow={renderRow}
        sortBy={sortBy}
        onSort={setSortBy}
        emptyMessage="Aucune action réalisée pour le moment."
        storageKey="actions_table"
      />
      {deleteItem && (
        <ConfirmDialog
          message={deleteItem._type === "todo" ? "Supprimer ce To Do réalisé ?" : "Supprimer cette action réalisée ?"}
          onConfirm={handleDelete}
          onCancel={() => setDeleteItem(null)}
        />
      )}
    </div>
  );
}
